import { Component } from '@theme/component';

/**
 * Expandable collection description with 2-line clamp and Read More / Read Less.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} viewport - Height-clipped container
 * @property {HTMLElement} content - Rich text content
 * @property {HTMLButtonElement} toggle - Toggle button
 */

class CollectionDescriptionExpand extends Component {
  requiredRefs = ['viewport', 'content', 'toggle'];

  /** @type {boolean} */
  #expanded = false;

  /** @type {number} */
  #collapsedHeight = 0;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();
    this.refs.toggle.addEventListener('click', this.#onToggle);
    requestAnimationFrame(() => this.#init());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
    this.refs.toggle.removeEventListener('click', this.#onToggle);
  }

  /**
   * @returns {number}
   */
  #getCollapsedLineCount() {
    const lines = Number.parseInt(
      getComputedStyle(this).getPropertyValue('--collection-description-lines'),
      10
    );
    return Number.isFinite(lines) && lines > 0 ? lines : 2;
  }

  /**
   * @returns {number}
   */
  #measureCollapsedHeight() {
    const { content } = this.refs;
    const style = getComputedStyle(content);
    const lineHeight = Number.parseFloat(style.lineHeight);

    if (!Number.isFinite(lineHeight) || lineHeight <= 0) {
      const fontSize = Number.parseFloat(style.fontSize) || 16;
      return Math.ceil(fontSize * 1.5 * this.#getCollapsedLineCount());
    }

    return Math.ceil(lineHeight * this.#getCollapsedLineCount());
  }

  #applyCollapsedHeight() {
    const { viewport } = this.refs;
    this.#collapsedHeight = this.#measureCollapsedHeight();
    viewport.style.maxHeight = `${this.#collapsedHeight}px`;
  }

  #init = () => {
    const { content } = this.refs;

    if (!content.textContent?.trim()) {
      this.hidden = true;
      return;
    }

    this.#expanded = false;
    this.dataset.expanded = 'false';
    this.#applyCollapsedHeight();
    this.#syncToggleLabel();
    this.#updateToggleVisibility();

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(() => {
        if (!this.#expanded) {
          this.#applyCollapsedHeight();
          this.#updateToggleVisibility();
        }
      });
      this.#resizeObserver.observe(content);
    }
  };

  #contentOverflows() {
    const { content } = this.refs;
    return content.scrollHeight > this.#collapsedHeight + 1;
  }

  #updateToggleVisibility() {
    const { viewport, toggle } = this.refs;
    const overflows = this.#contentOverflows();

    toggle.hidden = !overflows;
    this.dataset.truncate = overflows ? 'true' : 'false';

    if (!overflows) {
      viewport.style.maxHeight = 'none';
      return;
    }

    if (!this.#expanded) {
      this.#applyCollapsedHeight();
    }
  }

  #syncToggleLabel() {
    const { toggle } = this.refs;
    const readMore = this.dataset.readMoreLabel || 'Read More';
    const readLess = this.dataset.readLessLabel || 'Read Less';
    toggle.textContent = this.#expanded ? readLess : readMore;
    toggle.setAttribute('aria-expanded', String(this.#expanded));
  }

  #onToggle = (event) => {
    event.preventDefault();

    const { viewport, content } = this.refs;

    if (!this.#expanded) {
      const startHeight = viewport.offsetHeight;
      this.#expanded = true;
      this.dataset.expanded = 'true';
      this.#syncToggleLabel();

      const endHeight = content.scrollHeight;
      viewport.style.maxHeight = `${startHeight}px`;

      requestAnimationFrame(() => {
        viewport.style.maxHeight = `${endHeight}px`;
      });

      const onTransitionEnd = (event) => {
        if (event.propertyName !== 'max-height') return;
        if (this.#expanded) viewport.style.maxHeight = 'none';
        viewport.removeEventListener('transitionend', onTransitionEnd);
      };
      viewport.addEventListener('transitionend', onTransitionEnd);
    } else {
      const startHeight = viewport.offsetHeight;
      this.#expanded = false;
      this.dataset.expanded = 'false';
      this.#syncToggleLabel();
      this.#applyCollapsedHeight();

      const endHeight = this.#collapsedHeight;
      viewport.style.maxHeight = `${startHeight}px`;

      requestAnimationFrame(() => {
        viewport.style.maxHeight = `${endHeight}px`;
      });

      const onTransitionEnd = (event) => {
        if (event.propertyName !== 'max-height') return;
        viewport.removeEventListener('transitionend', onTransitionEnd);
      };
      viewport.addEventListener('transitionend', onTransitionEnd);
    }
  };
}

if (!customElements.get('collection-description-expand')) {
  customElements.define('collection-description-expand', CollectionDescriptionExpand);
}
